import {ChangeDetectionStrategy, Component, computed, effect, inject, QueryList, ViewChildren} from '@angular/core';

import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatTabsModule} from '@angular/material/tabs';
import {
  ScreeningResearchSettingsFieldComponent
} from '../screening-research-settings-field/screening-research-settings-field.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Store} from '@ngrx/store';
import {ScreeningSettingsDTO, ScreeningSettingsResearchDBDTO} from '../../../dto/screening-settings';
import {selectSettingByProjectId} from '../../../services/screening-settings/screening-settings.selectors';
import {updateScreeningSetting} from '../../../services/screening-settings/screening-settings.actions';
import {ConfirmDialogComponent} from '../../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {deleteAllResearchPaper} from '../../../services/research-paper/research-paper.actions';
import {ResearchSource} from '../../../dto/research-paper';
import {ProjectPermissionService} from '../../../../../utils/services/project-permission.service';
import {selectSelectedProject} from '../../../../project/service/project.selectors';
import {of} from 'rxjs';
import {LoadingCardComponent} from '../../../../../utils/components/loading-card/loading-card.component';
import {selectKeywordsByProjectId} from '../../../services/research-keyword/research-keyword.selectors';
import {ResearchKeywordDTO} from '../../../dto/research-keywords';
import {MatStepperModule} from '@angular/material/stepper';
import {ErrorCardComponent} from '../../../../../utils/components/error-card/error-card.component';
import {hasPubmedAPIKey, hasScopusAPIKey, hasSemanticAPIKey} from '../../../../../utils/models/llm-setting';
import {selectPubmedLocalEnabled} from '../../../../global-config/global-config.selectors';
import {
  LlmModelSettingDialogComponent
} from '../../../../../utils/components/llm-model-setting-dialog/llm-model-setting-dialog.component';
import {SaveBtnComponent} from '../../../../../utils/components/save-btn/save-btn.component';
import {EmptyStateComponent} from '../../../../../utils/components/empty-state/empty-state.component';
import {InfoCardComponent} from '../../../../../utils/components/info-card/info-card.component';
import {HeaderComponent} from '../../../../../utils/components/header/header.component';
import {
  SearchSettingsDatabaseOverviewDialogComponent
} from '../search-settings-database-overview-dialog/search-settings-database-overview-dialog.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-search-settings',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatDividerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTabsModule,
    ScreeningResearchSettingsFieldComponent,
    RouterLink,
    LoadingCardComponent,
    MatStepperModule,
    ErrorCardComponent,
    SaveBtnComponent,
    EmptyStateComponent,
    InfoCardComponent,
    HeaderComponent
  ],
  templateUrl: './search-settings.component.html',
  styleUrl: './search-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchSettingsComponent {
  private readonly store: Store = inject(Store);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);

  @ViewChildren('researchSettings') components?: QueryList<ScreeningResearchSettingsFieldComponent>;

  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly projectId = computed(() => this.project()?.id);
  readonly editable = computed(() =>
    this.projectPermissionService.hasWritePermission(this.project() ?? undefined)
  );
  readonly keywords = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectKeywordsByProjectId(projectId)) : of([] as ResearchKeywordDTO[]))
    ),
    {initialValue: [] as ResearchKeywordDTO[]}
  );
  readonly setting = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectSettingByProjectId(projectId)) : of(undefined as ScreeningSettingsDTO | undefined))
    ),
    {initialValue: undefined as ScreeningSettingsDTO | undefined}
  );
  readonly noKeywords = computed(() => !this.projectId() || this.keywords().length === 0);
  readonly hasSettings = computed(() => !!this.setting());

  private researchDBSettings: ScreeningSettingsResearchDBDTO[] = [];
  form: FormGroup = this.fb.group(
    {
      researchPubmedSettings: this.fb.array([]),
      researchSemanticSettings: this.fb.array([]),
      researchScopusSettings: this.fb.array([]),
      enablePubMedQuery: [false],
      enableScopusQuery: [false],
      enableSemanticScholarQuery: [false],
      enableYearFilter: [false],
      fromYear: ['', [Validators.min(1900)]],
      toYear: ['', [Validators.min(1900)]],
      maxResults: [2000, [Validators.min(100), Validators.max(10000)]],
    },
    {validators: [this.yearRangeValidator]}
  );

  readonly pubmedLocalEnabled = this.store.selectSignal(selectPubmedLocalEnabled);
  hasPubMedApiKey: boolean = false;
  hasSemanticScholarApiKey: boolean = false;
  hasScopusApiKey: boolean = false;
  readonly enableYearFilter = toSignal(this.form.get('enableYearFilter')!.valueChanges, {
    initialValue: this.form.get('enableYearFilter')!.value
  });

  constructor() {
    this.hasPubMedApiKey = hasPubmedAPIKey();
    this.hasSemanticScholarApiKey = hasSemanticAPIKey();
    this.hasScopusApiKey = hasScopusAPIKey();

    effect(() => {
      const setting = this.setting();
      this.clearResearchSettingArrays();
      this.researchDBSettings = [];
      if (!setting) {
        return;
      }
      this.form.patchValue(setting, {emitEvent: false});
      this.researchDBSettings = [...setting.researchDBSettings];
      setting.researchDBSettings.forEach((researchSetting) => {
        this.addResearchSetting(researchSetting.source, true);
      });
      this.changeYearEnabled(setting.enableYearFilter);
    });

    effect(() => {
      this.changeYearEnabled(this.enableYearFilter());
    });

    effect(() => {
      if (this.editable()) {
        this.form.enable({emitEvent: false});
      } else {
        this.form.disable({emitEvent: false});
      }
      this.changeYearEnabled(this.form.get('enableYearFilter')?.value);
    });
  }

  openDatabaseOverview() {
    this.dialog.open(SearchSettingsDatabaseOverviewDialogComponent, {
      autoFocus: false,
      height: '600px',
      width: '600px',
      maxWidth: '100vw'
    });
  }

  changeYearEnabled(enabled: boolean) {
    const fromYearControl = this.form.get('fromYear');
    const toYearControl = this.form.get('toYear');

    if (enabled) {
      fromYearControl?.enable();
      toYearControl?.enable();
    } else {
      fromYearControl?.disable();
      toYearControl?.disable();
    }
    toYearControl?.updateValueAndValidity();
    fromYearControl?.updateValueAndValidity();

  }

  getResearchSettings(source: ResearchSource): FormArray {
    if (source === ResearchSource.PUBMED) {
      return this.form.get('researchPubmedSettings') as FormArray;
    }
    if (source === ResearchSource.SEMANTIC_SCHOLAR) {
      return this.form.get('researchSemanticSettings') as FormArray;
    }
    if (source === ResearchSource.SCOPUS) {
      return this.form.get('researchScopusSettings') as FormArray;
    }
    return this.fb.array([]);
  }

  get settingId(): number {
    return this.setting()?.id || 0;
  }

  getResearchSettingGroup(index: number, source: ResearchSource): FormGroup {
    return this.getResearchSettings(source).controls[index] as FormGroup;
  }

  addResearchSetting(source: ResearchSource, ignoreEditable?: boolean): void {
    if (ignoreEditable || this.editable()) {
      const newSetting = this.fb.group({
        query: ['', Validators.required],
        queryType: ['', Validators.required],
      });
      this.getResearchSettings(source).push(newSetting);
    }
  }

  getScreeningSettingsResearchDBDTO(index: number, source: ResearchSource): ScreeningSettingsResearchDBDTO | undefined {
    if (!this.setting()) {
      return undefined;
    }
    return this.researchDBSettings
      .filter((setting) => setting.source === source)[index];
  }

  removeResearchSetting(index: number, source: ResearchSource, settingId: number | undefined): void {
    if (!this.editable()) {
      return;
    }
    this.getResearchSettings(source).removeAt(index);
    if (this.setting() && settingId) {
      this.researchDBSettings = this.researchDBSettings
        .filter((setting) => setting.id !== settingId);
    }
  }


  save(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    if (!this.editable()) {
      return;
    }
    const researchDBSettings: ScreeningSettingsResearchDBDTO[] = [];
    if (this.components) {
      this.components.forEach(component => {
        researchDBSettings.push(component.getScreeningSettingsResearchDBDTO());
      });
    }
    const formValue = {...this.form.value};
    delete formValue.researchPubmedSettings;
    delete formValue.researchSemanticSettings;
    delete formValue.researchScopusSettings;

    const updatedSettings: ScreeningSettingsDTO = {...this.setting(), ...formValue, researchDBSettings};
    this.store.dispatch(() => updateScreeningSetting(
      {
        projectId,
        id: updatedSettings.id,
        setting: updatedSettings
      }));
  }

  yearRangeValidator(group: AbstractControl) {
    const fromYear = group.get('fromYear')?.value;
    const toYear = group.get('toYear')?.value;
    if (!fromYear || !toYear || toYear >= fromYear) {
      return null; // Valid
    }
    return {yearRange: true}; // Invalid
  }

  openSettings() {
    this.dialog.open(LlmModelSettingDialogComponent, {
      autoFocus: false,
      height: '600px',
      width: '600px',
      maxWidth: '100vw'
    });
  }

  deleteAllPaper() {
    const projectId = this.projectId();
    if (!this.editable() || !projectId) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Delete all Paper',
        warningMessage: 'All paper in this project will be deleted',
        confirmButtonText: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.store.dispatch(deleteAllResearchPaper({projectId}));
    });
  }

  navigateToNextStep(): void {
    this.router.navigate(['../search-setting'], {relativeTo: this.route});

  }

  protected readonly ResearchSource = ResearchSource;

  private clearResearchSettingArrays(): void {
    this.getResearchSettings(ResearchSource.PUBMED).clear();
    this.getResearchSettings(ResearchSource.SEMANTIC_SCHOLAR).clear();
    this.getResearchSettings(ResearchSource.SCOPUS).clear();
  }
}
